// src/components/SchemaExplorer.tsx
// Reusable schema explorer:
//   - List view with column details & PK/FK badges
//   - ER diagram: clean light cards, correct bezier relationship lines, cross-schema FK awareness
//   - Multi-schema support via extraSchemas prop — loads & displays tables from multiple schemas
//   - Multi-table selection with relationship awareness
//   - SQL panel with auto-generate JOIN query

import React, {
  useState, useEffect, useRef, useCallback, useMemo
} from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ColumnDef {
  name: string;
  data_type: string;
  nullable: boolean;
  is_primary_key: boolean;
  is_foreign_key?: boolean;
  fk_schema?: string;   // schema of referenced table — may differ from current schema
  fk_table?: string;
  fk_column?: string;
  max_length?: number | null;
}

export interface TableDef {
  name: string;
  schema: string;
  row_count?: number | null;
  size_bytes?: number | null;
  columns: ColumnDef[];
}

export interface FKRelationship {
  from_schema: string;
  from_table: string;
  from_column: string;
  to_schema: string;
  to_table: string;
  to_column: string;
}

export interface SchemaSelection {
  tables: string[];
  /** Maps bare table name → its schema. Needed when tables span multiple schemas. */
  tableSchemas?: Record<string, string>;
  columns: Record<string, string[]>;
  relationships: FKRelationship[];
  custom_sql?: string;
}

export interface SchemaExplorerProps {
  connectionId: string;
  /** Primary schema to browse */
  schema: string;
  /** Additional schemas to load — used to resolve cross-schema FK targets */
  extraSchemas?: string[];
  onSelectionConfirm: (selection: SchemaSelection) => void;
  onBack?: () => void;
  apiBase?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fqKey = (schema: string, table: string) => `${schema}.${table}`;

function fmtRows(n?: number | null) {
  if (n == null) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function fmtBytes(b?: number | null) {
  if (!b) return null;
  if (b < 1024)    return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

function shortType(t: string) {
  return t
    .replace(/character varying(\(\d+\))?/i, "varchar$1")
    .replace(/timestamp without time zone/i, "timestamp")
    .replace(/timestamp with time zone/i, "timestamptz")
    .replace(/double precision/i, "float8")
    .replace(/boolean/i, "bool");
}

// ─── API ──────────────────────────────────────────────────────────────────────

function useApi() {
  //const base = (process.env.REACT_APP_API_BASE || "") + "/adriva_api/connections";
  const base = ("http://localhost:3003") + "/adriva_api/connections";
  const get = useCallback(async (path: string) => {
    const r = await fetch(base + path, { headers: { "Content-Type": "application/json" } });
    if (!r.ok) throw new Error(await r.text());
    return r.json();
  }, [base]);
  return { get };
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

const Spinner = ({ size = 16 }: { size?: number }) => (
  <div style={{
    width: size, height: size, borderRadius: "50%",
    border: `${size < 20 ? 2 : 3}px solid #e2e8f0`,
    borderTopColor: "#6366f1",
    animation: "se-spin 0.7s linear infinite",
    flexShrink: 0,
  }} />
);

// ─── CSS ──────────────────────────────────────────────────────────────────────

const SE_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=DM+Sans:wght@400;500;600;700&display=swap');

  @keyframes se-spin   { to { transform: rotate(360deg); } }
  @keyframes se-fadein { from { opacity:0; transform:translateY(5px); } to { opacity:1; transform:translateY(0); } }
  @keyframes se-pop    { from { opacity:0; transform:scale(0.94); } to { opacity:1; transform:scale(1); } }

  .se-root { font-family:'DM Sans',sans-serif; color:#0f172a; height:100%; display:flex; flex-direction:column; overflow:auto;  }
  .se-root * { box-sizing:border-box; }

  /* toolbar */
  .se-toolbar {
    display:flex; align-items:center; gap:10px; flex-wrap:wrap;
    padding:9px 14px; background:#f8fafc; border-bottom:1px solid #e2e8f0; flex-shrink:0;
  }
  .se-toolbar-title { font-family:'IBM Plex Mono',monospace; font-size:11px; font-weight:600; letter-spacing:1.2px; text-transform:uppercase; color:#64748b; }
  .se-view-toggle { display:flex; background:#e2e8f0; border-radius:8px; padding:2px; gap:2px; margin-left:auto; }
  .se-view-btn {
    all:unset; box-sizing:border-box; cursor:pointer;
    padding:5px 12px; border-radius:6px; font-size:12px; font-weight:600;
    color:#64748b; transition:all 0.15s; display:flex; align-items:center; gap:5px;
  }
  .se-view-btn.active { background:#fff; color:#4f46e5; box-shadow:0 1px 3px rgba(0,0,0,0.1); }
  .se-view-btn:hover:not(.active) { color:#334155; }
  .se-badge { font-size:10px; padding:2px 7px; border-radius:99px; font-weight:700; background:#4f46e5; color:#fff; font-family:'IBM Plex Mono',monospace; }
  .se-btn {
    all:unset; box-sizing:border-box; cursor:pointer;
    display:inline-flex; align-items:center; gap:6px;
    padding:7px 14px; border-radius:8px; font-family:'DM Sans',sans-serif; font-size:12.5px; font-weight:600; transition:all 0.13s;
  }
  .se-btn-primary { background:#4f46e5; color:#fff; }
  .se-btn-primary:hover { background:#4338ca; }
  .se-btn-primary:disabled { background:#c7d2fe; color:#fff; cursor:not-allowed; }
  .se-btn-ghost { border:1.5px solid #e2e8f0; color:#475569; background:#fff; }
  .se-btn-ghost:hover { border-color:#c7d2fe; color:#4f46e5; background:#f5f3ff; }
  .se-search { display:flex; align-items:center; gap:8px; background:#fff; border:1.5px solid #e2e8f0; border-radius:8px; padding:5px 10px; transition:border-color 0.15s; }
  .se-search:focus-within { border-color:#818cf8; }
  .se-search input { all:unset; font-family:'DM Sans',sans-serif; font-size:13px; color:#0f172a; width:150px; }
  .se-search input::placeholder { color:#94a3b8; }

  /* layout */
  .se-split { display:flex; flex:1; overflow:hidden; min-height:0; }
  .se-sidebar {
    width:230px; flex-shrink:0; border-right:1px solid #e2e8f0;
    display:flex; flex-direction:column; background:#fafafa; overflow:hidden;
    min-height:0;
  }
  .se-sidebar-hd {
    padding:9px 12px; font-family:'IBM Plex Mono',monospace; font-size:9px; font-weight:600;
    letter-spacing:1.5px; text-transform:uppercase; color:#94a3b8;
    border-bottom:1px solid #f1f5f9; display:flex; align-items:center; justify-content:space-between;
    flex-shrink:0;
  }
  .se-sidebar-list { flex:1; overflow-y:auto; min-height:0; }
  .se-sidebar-list::-webkit-scrollbar { width:3px; }
  .se-sidebar-list::-webkit-scrollbar-thumb { background:#e2e8f0; border-radius:99px; }

  .se-schema-group-hd {
    padding:6px 10px 6px 12px;
    font-family:'IBM Plex Mono',monospace; font-size:8.5px; font-weight:700;
    letter-spacing:1.2px; text-transform:uppercase; color:#6366f1;
    background:#f0f0fe; border-bottom:1px solid #e0e7ff; border-top:1px solid #e0e7ff;
    position:sticky; top:0; z-index:1;
    display:flex; align-items:center; justify-content:space-between;
    cursor:pointer; user-select:none;
  }
  .se-schema-group-hd:hover { background:#e8e8fc; }
  .se-schema-group-hd:first-child { border-top:none; }
  .se-schema-chevron { font-size:9px; color:#a5b4fc; line-height:1; transition:transform 0.15s; display:inline-block; }
  .se-schema-chevron.open { transform:rotate(90deg); }

  .se-table-row {
    display:flex; align-items:center; gap:9px;
    padding:8px 12px; cursor:pointer; border-bottom:1px solid #f1f5f9; transition:background 0.1s; user-select:none;
  }
  .se-table-row:hover   { background:#f0f4ff; }
  .se-table-row.active  { background:#eef2ff; border-left:2px solid #4f46e5; }
  .se-table-row.checked { background:#f0fdf4; }
  .se-table-chk {
    width:16px; height:16px; border-radius:4px; flex-shrink:0;
    border:1.5px solid #d1d5db; background:#fff; cursor:pointer;
    display:flex; align-items:center; justify-content:center; transition:all 0.12s;
  }
  .se-table-chk.checked { background:#4f46e5; border-color:#4f46e5; }
  .se-table-nm { font-size:12.5px; font-weight:600; color:#1e293b; flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .se-table-meta { font-family:'IBM Plex Mono',monospace; font-size:9px; color:#94a3b8; margin-top:1px; }

  .se-main { flex:1; overflow:hidden; position:relative; background:#f8fafc; }

  /* list view */
  .se-detail { padding:16px; overflow-y:auto; height:100%; }
  .se-detail::-webkit-scrollbar { width:4px; }
  .se-detail::-webkit-scrollbar-thumb { background:#e2e8f0; border-radius:99px; }
  .se-detail-hd { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:14px; gap:12px; }
  .se-detail-title { font-size:17px; font-weight:700; color:#0f172a; letter-spacing:-0.3px; }
  .se-detail-sub { font-family:'IBM Plex Mono',monospace; font-size:10px; color:#94a3b8; margin-top:2px; }
  .se-detail-stats { display:flex; gap:8px; flex-wrap:wrap; margin-top:6px; }
  .se-stat { font-family:'IBM Plex Mono',monospace; font-size:10px; padding:3px 8px; border-radius:5px; background:#f1f5f9; color:#475569; }
  .se-col-table { width:100%; border-collapse:collapse; font-size:12.5px; animation:se-fadein 0.2s ease; }
  .se-col-table thead th {
    text-align:left; padding:7px 10px;
    font-family:'IBM Plex Mono',monospace; font-size:9px; font-weight:600;
    letter-spacing:1.2px; text-transform:uppercase; color:#94a3b8;
    background:#f8fafc; border-bottom:1.5px solid #e2e8f0; position:sticky; top:0;
  }
  .se-col-table tbody tr { transition:background 0.1s; cursor:pointer; }
  .se-col-table tbody tr:hover { background:#f0f4ff; }
  .se-col-table tbody tr.col-sel { background:#eef2ff; }
  .se-col-table tbody td { padding:7px 10px; border-bottom:1px solid #f1f5f9; vertical-align:middle; }
  .se-col-name { font-weight:600; color:#1e293b; display:flex; align-items:center; gap:5px; }
  .se-pk-tag { font-family:'IBM Plex Mono',monospace; font-size:8px; padding:1px 5px; border-radius:3px; background:#fef3c7; color:#92400e; font-weight:700; }
  .se-fk-tag { background:#dbeafe; color:#1e40af; }
  .se-nn-tag { background:#fee2e2; color:#991b1b; font-size:8px; padding:1px 5px; border-radius:3px; font-family:'IBM Plex Mono',monospace; font-weight:700; }
  .se-type-chip { font-family:'IBM Plex Mono',monospace; font-size:10px; padding:2px 7px; border-radius:4px; background:#f1f5f9; color:#475569; white-space:nowrap; }
  .se-fk-ref { font-family:'IBM Plex Mono',monospace; font-size:10px; color:#4f46e5; display:flex; align-items:center; gap:4px; cursor:pointer; }
  .se-fk-ref:hover { text-decoration:underline; }
  .se-col-chk { width:14px; height:14px; border-radius:3px; flex-shrink:0; border:1.5px solid #d1d5db; background:#fff; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all 0.12s; margin:auto; }
  .se-col-chk.checked { background:#4f46e5; border-color:#4f46e5; }

  /* ER canvas */
  .se-er-wrap { width:100%; height:100%; position:relative; overflow:hidden; }
  .se-er-canvas {
    width:100%; height:100%; overflow:hidden; position:relative; cursor:grab;
    background:#f8fafc;
    background-image: linear-gradient(#e8ecf0 1px, transparent 1px), linear-gradient(90deg, #e8ecf0 1px, transparent 1px);
    background-size:40px 40px;
  }
  .se-er-canvas.grabbing { cursor:grabbing; }
  .se-er-canvas svg { position:absolute; top:0; left:0; pointer-events:none; overflow:visible; }

  /* ER cards — single clean palette */
  .se-er-card {
    position:absolute; border-radius:8px; overflow:hidden;
    background:#ffffff; border:1.5px solid #e2e8f0;
    box-shadow:0 1px 4px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04);
    cursor:grab; user-select:none; animation:se-pop 0.18s ease; min-width:190px;
    transition:border-color 0.15s, box-shadow 0.15s;
  }
  .se-er-card:hover { border-color:#c7d2fe; box-shadow:0 3px 12px rgba(99,102,241,0.1); }
  .se-er-card.sel   { border-color:#6366f1; box-shadow:0 0 0 2px rgba(99,102,241,0.15), 0 3px 12px rgba(99,102,241,0.12); }
  .se-er-card.dragging { cursor:grabbing; box-shadow:0 8px 28px rgba(0,0,0,0.12); z-index:100; }
  .se-er-card.cross-schema { border-style:dashed; background:#fafaff; }

  .se-er-hd {
    padding:8px 10px 7px; display:flex; align-items:center; gap:7px;
    background:#f8fafc; border-bottom:1.5px solid #e2e8f0;
  }
  .se-er-card.sel .se-er-hd { background:#eef2ff; border-bottom-color:#c7d2fe; }
  .se-er-card.cross-schema .se-er-hd { background:#faf5ff; border-bottom-color:#ede9fe; }
  .se-er-hd-schema { font-family:'IBM Plex Mono',monospace; font-size:7.5px; font-weight:700; color:#a855f7; letter-spacing:0.5px; text-transform:uppercase; margin-bottom:1px; }
  .se-er-hd-name { font-family:'IBM Plex Mono',monospace; font-size:11.5px; font-weight:700; color:#1e293b; flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .se-er-card.sel .se-er-hd-name { color:#4338ca; }
  .se-er-hd-cnt { font-family:'IBM Plex Mono',monospace; font-size:9px; color:#94a3b8; flex-shrink:0; }
  .se-er-chk {
    width:14px; height:14px; border-radius:3px; flex-shrink:0; cursor:pointer;
    border:1.5px solid #d1d5db; background:#fff;
    display:flex; align-items:center; justify-content:center; transition:all 0.12s;
  }
  .se-er-chk.sel { background:#6366f1; border-color:#6366f1; }

  .se-er-col {
    display:flex; align-items:center; gap:6px;
    padding:3px 10px 3px 8px; border-bottom:1px solid #f8fafc;
  }
  .se-er-col:last-child { border-bottom:none; }
  .se-er-col.pk { background:#fffbeb; }
  .se-er-col.fk { background:#eff6ff; }
  .se-er-col-icon { font-size:9px; width:11px; text-align:center; flex-shrink:0; }
  .se-er-col-name { flex:1; font-size:11px; font-weight:500; color:#374151; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .se-er-col.pk .se-er-col-name { color:#92400e; font-weight:600; }
  .se-er-col.fk .se-er-col-name { color:#1e40af; }
  .se-er-col-type { font-family:'IBM Plex Mono',monospace; font-size:8.5px; color:#94a3b8; flex-shrink:0; }
  .se-er-more { padding:4px 10px; font-family:'IBM Plex Mono',monospace; font-size:8.5px; color:#94a3b8; background:#f8fafc; border-top:1px solid #f1f5f9; }
  .se-er-loading { padding:6px 10px; font-size:10px; color:#94a3b8; font-family:'IBM Plex Mono',monospace; font-style:italic; }

  /* ER controls */
  .se-er-controls { position:absolute; bottom:14px; right:14px; display:flex; flex-direction:column; gap:4px; z-index:10; }
  .se-er-ctrl {
    all:unset; box-sizing:border-box; cursor:pointer;
    width:30px; height:30px; border-radius:7px;
    background:rgba(255,255,255,0.97); border:1px solid #e2e8f0;
    display:flex; align-items:center; justify-content:center;
    font-size:13px; color:#475569; transition:all 0.12s;
    box-shadow:0 1px 4px rgba(0,0,0,0.07);
  }
  .se-er-ctrl:hover { color:#4f46e5; border-color:#c7d2fe; }
  .se-er-zoom-label {
    height:24px; display:flex; align-items:center; justify-content:center;
    background:rgba(255,255,255,0.9); border:1px solid #e2e8f0; border-radius:6px;
    font-family:'IBM Plex Mono',monospace; font-size:9px; color:#94a3b8;
    box-shadow:0 1px 3px rgba(0,0,0,0.05);
  }

  /* ER legend */
  .se-er-legend {
    position:absolute; bottom:14px; left:14px; z-index:10;
    background:rgba(255,255,255,0.97); border:1px solid #e2e8f0; border-radius:8px;
    padding:8px 12px; font-size:10.5px; color:#475569;
    box-shadow:0 2px 8px rgba(0,0,0,0.06);
  }
  .se-er-legend-title { font-weight:700; font-size:10px; color:#374151; margin-bottom:5px; }
  .se-er-legend-row { display:flex; align-items:center; gap:7px; margin-top:3px; }

  /* ER schema pills */
  .se-er-schema-pills { position:absolute; top:12px; left:12px; z-index:10; display:flex; gap:6px; flex-wrap:wrap; max-width:400px; }
  .se-er-pill {
    all:unset; box-sizing:border-box; cursor:pointer;
    padding:3px 10px; border-radius:99px; font-size:10px; font-weight:600;
    border:1.5px solid #e2e8f0; background:#fff; color:#64748b; transition:all 0.12s;
  }
  .se-er-pill:hover { border-color:#c7d2fe; color:#4f46e5; }
  .se-er-pill.on { background:#4f46e5; color:#fff; border-color:#4f46e5; }

  /* footer */
  .se-sel-footer { padding:9px 14px; background:#1e1b4b; border-top:1px solid #312e81; display:flex; align-items:center; gap:12px; flex-shrink:0; animation:se-fadein 0.2s ease; }
  .se-sel-chips { display:flex; flex-wrap:wrap; gap:6px; flex:1; }
  .se-sel-chip { display:flex; align-items:center; gap:5px; padding:3px 10px; border-radius:99px; font-size:11px; font-weight:600; background:rgba(255,255,255,0.12); color:#e0e7ff; border:1px solid rgba(255,255,255,0.15); }
  .se-sel-rm { all:unset; cursor:pointer; font-size:11px; color:#a5b4fc; width:14px; height:14px; display:flex; align-items:center; justify-content:center; border-radius:50%; transition:all 0.1s; }
  .se-sel-rm:hover { background:rgba(255,255,255,0.2); color:#fff; }

  /* SQL */
  .se-sql-panel { padding:16px; display:flex; flex-direction:column; gap:10px; height:100%; }
  .se-sql-area {
    flex:1; width:100%; padding:12px;
    font-family:'IBM Plex Mono',monospace; font-size:12.5px; color:#0f172a;
    background:#fff; border:1.5px solid #e2e8f0; border-radius:8px;
    resize:none; outline:none; line-height:1.6; transition:border-color 0.15s;
  }
  .se-sql-area:focus { border-color:#818cf8; box-shadow:0 0 0 3px rgba(99,102,241,0.08); }
  .se-sql-hint { font-size:11.5px; color:#64748b; line-height:1.5; }

  /* states */
  .se-loading { display:flex; align-items:center; justify-content:center; height:100%; gap:10px; color:#64748b; font-size:13.5px; }
  .se-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; color:#94a3b8; gap:10px; text-align:center; padding:40px; }
  .se-empty-icon { font-size:36px; }
  .se-empty-title { font-size:15px; font-weight:600; color:#64748b; }
  .se-empty-sub { font-size:12.5px; line-height:1.6; max-width:320px; }
`;

// ─── ER Diagram layout ────────────────────────────────────────────────────────

const CARD_W   = 200;
const COL_H    = 24;
const HEADER_H = 44;
const MAX_COLS = 14;

function cardH(t: TableDef) {
  return HEADER_H + Math.min(t.columns.length, MAX_COLS) * COL_H + (t.columns.length > MAX_COLS ? 20 : 0) + 4;
}

function portR(pos: { x: number; y: number }, t: TableDef, col: string) {
  const idx = t.columns.findIndex(c => c.name === col);
  const i   = idx < 0 ? 0 : Math.min(idx, MAX_COLS - 1);
  return { x: pos.x + CARD_W, y: pos.y + HEADER_H + i * COL_H + COL_H / 2 };
}

function portL(pos: { x: number; y: number }, t: TableDef, col: string) {
  const idx = t.columns.findIndex(c => c.name === col);
  const i   = idx < 0 ? 0 : Math.min(idx, MAX_COLS - 1);
  return { x: pos.x, y: pos.y + HEADER_H + i * COL_H + COL_H / 2 };
}

function autoLayout(tables: TableDef[], rels: FKRelationship[]) {
  const adj: Record<string, Set<string>> = {};
  for (const t of tables) adj[fqKey(t.schema, t.name)] = new Set();
  for (const r of rels) {
    const fk = fqKey(r.from_schema, r.from_table);
    const tk = fqKey(r.to_schema,   r.to_table);
    adj[fk]?.add(tk); adj[tk]?.add(fk);
  }

  const visited = new Set<string>();
  const clusters: string[][] = [];
  for (const t of tables) {
    const k = fqKey(t.schema, t.name);
    if (visited.has(k)) continue;
    const cluster: string[] = [];
    const queue = [k];
    while (queue.length) {
      const cur = queue.shift()!;
      if (visited.has(cur)) continue;
      visited.add(cur); cluster.push(cur);
      for (const nb of (adj[cur] || [])) queue.push(nb);
    }
    clusters.push(cluster);
  }

  const pos: Record<string, { x: number; y: number }> = {};
  let ox = 60, oy = 60;
  const H_GAP = 260, V_GAP = 300, C_GAP = 70;
  for (const cluster of clusters) {
    const cols = Math.max(1, Math.ceil(Math.sqrt(cluster.length)));
    let maxH = 0;
    cluster.forEach((key, i) => {
      const c = i % cols, r = Math.floor(i / cols);
      pos[key] = { x: ox + c * H_GAP, y: oy + r * V_GAP };
      const t = tables.find(tb => fqKey(tb.schema, tb.name) === key);
      maxH = Math.max(maxH, r * V_GAP + (t ? cardH(t) : 200));
    });
    oy += maxH + C_GAP;
    if (oy > 1400) { oy = 60; ox += cols * H_GAP + C_GAP; }
  }
  return pos;
}

// ─── ER Diagram Component ─────────────────────────────────────────────────────

interface ERDiagramProps {
  tables: TableDef[];
  allSchemas: string[];
  visibleSchemas: Set<string>;
  onToggleSchema: (s: string) => void;
  selectedTables: Set<string>;
  relationships: FKRelationship[];
  onTableToggle: (schema: string, name: string) => void;
}

const ERDiagram: React.FC<ERDiagramProps> = ({
  tables, allSchemas, visibleSchemas, onToggleSchema,
  selectedTables, relationships, onTableToggle,
}) => {
  const ref    = useRef<HTMLDivElement>(null);
  const [pos, setPos]         = useState<Record<string, { x: number; y: number }>>({});
  const [zoom, setZoom]       = useState(0.85);
  const [pan, setPan]         = useState({ x: 40, y: 40 });
  const [panning, setPanning] = useState(false);
  const [panSt, setPanSt]     = useState({ x: 0, y: 0 });
  const [drag, setDrag]       = useState<{ key: string; ox: number; oy: number } | null>(null);
  const [svgSz, setSvgSz]     = useState({ w: 2000, h: 2000 });

  const visible = useMemo(() => tables.filter(t => visibleSchemas.has(t.schema)), [tables, visibleSchemas]);

  useEffect(() => {
    if (!visible.length) return;
    const visRels = relationships.filter(r => visibleSchemas.has(r.from_schema) && visibleSchemas.has(r.to_schema));
    const layout  = autoLayout(visible, visRels);
    setPos(prev => {
      const merged = { ...layout };
      // Preserve manually dragged positions
      for (const [k, v] of Object.entries(prev)) { if (k in merged) merged[k] = v; }
      return merged;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible.map(t => fqKey(t.schema, t.name)).join(","), relationships.length]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new ResizeObserver(() => setSvgSz({ w: el.offsetWidth, h: el.offsetHeight }));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const onDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest(".se-er-card")) return;
    setPanning(true);
    setPanSt({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };
  const onMove = (e: React.MouseEvent) => {
    if (drag) {
      const rect = ref.current!.getBoundingClientRect();
      setPos(p => ({ ...p, [drag.key]: {
        x: (e.clientX - rect.left - pan.x) / zoom - drag.ox,
        y: (e.clientY - rect.top  - pan.y) / zoom - drag.oy,
      }}));
    } else if (panning) {
      setPan({ x: e.clientX - panSt.x, y: e.clientY - panSt.y });
    }
  };
  const onUp   = () => { setPanning(false); setDrag(null); };
  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    setZoom(z => Math.max(0.25, Math.min(2, z - e.deltaY * 0.0008)));
  };
  const onCardDown = (e: React.MouseEvent, key: string) => {
    e.stopPropagation();
    const p    = pos[key] || { x: 0, y: 0 };
    const rect = ref.current!.getBoundingClientRect();
    setDrag({ key, ox: (e.clientX - rect.left - pan.x) / zoom - p.x, oy: (e.clientY - rect.top - pan.y) / zoom - p.y });
  };

  // Build relationship lines
  const lines = useMemo(() => relationships.flatMap((rel, i) => {
    const fk  = fqKey(rel.from_schema, rel.from_table);
    const tk  = fqKey(rel.to_schema,   rel.to_table);
    const fT  = tables.find(t => t.schema === rel.from_schema && t.name === rel.from_table);
    const tT  = tables.find(t => t.schema === rel.to_schema   && t.name === rel.to_table);
    const fP  = pos[fk];
    const tP  = pos[tk];
    if (!fT || !tT || !fP || !tP) return [];
    if (!visibleSchemas.has(rel.from_schema) || !visibleSchemas.has(rel.to_schema)) return [];

    const from  = portR(fP, fT, rel.from_column);
    const to    = portL(tP, tT, rel.to_column);
    const dx    = Math.abs(to.x - from.x);
    const cpx   = Math.max(50, dx * 0.45);
    const path  = `M ${from.x} ${from.y} C ${from.x + cpx} ${from.y}, ${to.x - cpx} ${to.y}, ${to.x} ${to.y}`;

    const active      = selectedTables.has(fqKey(rel.from_schema, rel.from_table)) && selectedTables.has(fqKey(rel.to_schema, rel.to_table));
    const isCross     = rel.from_schema !== rel.to_schema;
    const activeColor = isCross ? "#a855f7" : "#6366f1";
    const color       = active ? activeColor : "#c8d4e0";
    const width       = active ? 2 : 1.5;
    const mx          = (from.x + to.x) / 2;
    const my          = (from.y + to.y) / 2;

    return [(
      <g key={i}>
        {active && <path d={path} fill="none" stroke={activeColor} strokeWidth={7} strokeOpacity={0.12} />}
        <path d={path} fill="none" stroke={color} strokeWidth={width} strokeDasharray={active ? undefined : "5,4"} />
        {/* crow's foot — many side */}
        <line x1={from.x+1} y1={from.y-5} x2={from.x+11} y2={from.y} stroke={color} strokeWidth="1.5" />
        <line x1={from.x+1} y1={from.y+5} x2={from.x+11} y2={from.y} stroke={color} strokeWidth="1.5" />
        {/* one side bar */}
        <line x1={to.x-8} y1={to.y-5} x2={to.x-8} y2={to.y+5} stroke={color} strokeWidth="1.5" />
        {/* arrow */}
        <polygon points={`${to.x-6},${to.y-4} ${to.x},${to.y} ${to.x-6},${to.y+4}`} fill={color} />
        {/* label */}
        {active && (
          <>
            <rect x={mx-43} y={my-9} width={86} height={16} rx={3} fill="white" fillOpacity={0.92} stroke={activeColor} strokeWidth={0.5} />
            <text x={mx} y={my+3} textAnchor="middle" fill={activeColor} fontSize="8" fontFamily="IBM Plex Mono, monospace" fontWeight="600">
              {rel.from_column} → {rel.to_column}
            </text>
          </>
        )}
      </g>
    )];
  }), [relationships, pos, selectedTables, tables, visibleSchemas]);

  if (!visible.length) {
    return (
      <div className="se-empty">
        <div className="se-empty-icon">🗺️</div>
        <div className="se-empty-title">No tables visible</div>
        <div className="se-empty-sub">Enable at least one schema using the pills above.</div>
      </div>
    );
  }

  return (
    <div className="se-er-wrap">
      {/* Schema pills */}
      {allSchemas.length > 1 && (
        <div className="se-er-schema-pills">
          {allSchemas.map(s => (
            <button key={s} className={`se-er-pill${visibleSchemas.has(s) ? " on" : ""}`} onClick={() => onToggleSchema(s)}>{s}</button>
          ))}
        </div>
      )}

      {/* Canvas */}
      <div ref={ref} className={`se-er-canvas${panning ? " grabbing" : ""}`}
        onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp} onWheel={onWheel}>
        <svg width={svgSz.w} height={svgSz.h} style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", overflow: "visible" }}>
          <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>{lines}</g>
        </svg>

        <div style={{ transform: `translate(${pan.x}px,${pan.y}px) scale(${zoom})`, transformOrigin: "0 0", position: "absolute", top: 0, left: 0 }}>
          {visible.map(t => {
            const key    = fqKey(t.schema, t.name);
            const p      = pos[key] || { x: 0, y: 0 };
            const isSel  = selectedTables.has(fqKey(t.schema, t.name));
            const isDrag = drag?.key === key;
            const isCross = allSchemas.length > 1 && t.schema !== allSchemas[0];
            const disp   = t.columns.slice(0, MAX_COLS);
            const over   = t.columns.length - MAX_COLS;

            return (
              <div key={key}
                className={["se-er-card", isSel ? "sel" : "", isDrag ? "dragging" : "", isCross ? "cross-schema" : ""].filter(Boolean).join(" ")}
                style={{ left: p.x, top: p.y, width: CARD_W }}
                onMouseDown={e => onCardDown(e, key)}>

                <div className="se-er-hd">
                  <div className={`se-er-chk${isSel ? " sel" : ""}`}
                    onClick={e => { e.stopPropagation(); onTableToggle(t.schema, t.name); }}>
                    {isSel && <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" /></svg>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {allSchemas.length > 1 && <div className="se-er-hd-schema">{t.schema}</div>}
                    <div className="se-er-hd-name">{t.name}</div>
                  </div>
                  {t.row_count != null && <span className="se-er-hd-cnt">{fmtRows(t.row_count)}</span>}
                </div>

                {t.columns.length > 0 ? (
                  <>
                    {disp.map(c => (
                      <div key={c.name} className={`se-er-col${c.is_primary_key ? " pk" : c.is_foreign_key ? " fk" : ""}`}>
                        <span className="se-er-col-icon">{c.is_primary_key ? "🔑" : c.is_foreign_key ? "🔗" : ""}</span>
                        <span className="se-er-col-name">{c.name}</span>
                        <span className="se-er-col-type">{shortType(c.data_type)}</span>
                      </div>
                    ))}
                    {over > 0 && <div className="se-er-more">+{over} more columns</div>}
                  </>
                ) : (
                  <div className="se-er-loading">columns loading…</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="se-er-legend">
        <div className="se-er-legend-title">Relationships</div>
        <div className="se-er-legend-row">
          <svg width="36" height="12"><path d="M0 6 C12 6 24 6 30 6" fill="none" stroke="#6366f1" strokeWidth="2"/><polygon points="27,3 33,6 27,9" fill="#6366f1"/></svg>
          FK (selected)
        </div>
        <div className="se-er-legend-row">
          <svg width="36" height="12"><path d="M0 6 C12 6 24 6 30 6" fill="none" stroke="#c8d4e0" strokeWidth="1.5" strokeDasharray="4,3"/><polygon points="27,3 33,6 27,9" fill="#c8d4e0"/></svg>
          FK (unselected)
        </div>
        {allSchemas.length > 1 && (
          <div className="se-er-legend-row">
            <svg width="36" height="12"><path d="M0 6 C12 6 24 6 30 6" fill="none" stroke="#a855f7" strokeWidth="2"/><polygon points="27,3 33,6 27,9" fill="#a855f7"/></svg>
            Cross-schema FK
          </div>
        )}
        <div className="se-er-legend-row" style={{ marginTop: 4, paddingTop: 4, borderTop: "1px solid #f1f5f9", gap: 5 }}>
          <span style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:3, padding:"1px 5px", fontSize:9, fontFamily:"IBM Plex Mono,monospace" }}>PK</span>
          <span style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:3, padding:"1px 5px", fontSize:9, fontFamily:"IBM Plex Mono,monospace" }}>FK</span>
        </div>
      </div>

      {/* Zoom controls */}
      <div className="se-er-controls">
        <button className="se-er-ctrl" onClick={() => setZoom(z => Math.min(2, z + 0.12))} title="Zoom in">＋</button>
        <div className="se-er-zoom-label">{Math.round(zoom * 100)}%</div>
        <button className="se-er-ctrl" onClick={() => setZoom(z => Math.max(0.25, z - 0.12))} title="Zoom out">－</button>
        <button className="se-er-ctrl" onClick={() => { setZoom(0.85); setPan({ x: 40, y: 40 }); }} title="Reset">⊡</button>
      </div>
    </div>
  );
};

// ─── Table Detail (List view) ─────────────────────────────────────────────────

interface TableDetailProps {
  table: TableDef | null;
  selectedCols: Set<string>;
  onColToggle: (col: string) => void;
  onSelectAllCols: () => void;
  onJumpToTable: (schema: string, name: string) => void;
}

const TableDetail: React.FC<TableDetailProps> = ({ table, selectedCols, onColToggle, onSelectAllCols, onJumpToTable }) => {
  if (!table) {
    return (
      <div className="se-empty">
        <div className="se-empty-icon">👈</div>
        <div className="se-empty-title">Select a table</div>
        <div className="se-empty-sub">Click any table in the sidebar to inspect its columns, types, and constraints.</div>
      </div>
    );
  }

  const allSel = table.columns.length > 0 && table.columns.every(c => selectedCols.has(c.name));
  return (
    <div className="se-detail">
      <div className="se-detail-hd">
        <div>
          <div className="se-detail-title">{table.name}</div>
          <div className="se-detail-sub">{table.schema}</div>
          <div className="se-detail-stats">
            <span className="se-stat">📊 {table.columns.length} cols</span>
            {table.row_count != null && <span className="se-stat">↕ {fmtRows(table.row_count)} rows</span>}
            {fmtBytes(table.size_bytes) && <span className="se-stat">💾 {fmtBytes(table.size_bytes)}</span>}
            <span className="se-stat">🔑 {table.columns.filter(c => c.is_primary_key).length} PK</span>
            <span className="se-stat">🔗 {table.columns.filter(c => c.is_foreign_key).length} FK</span>
          </div>
        </div>
        <button className="se-btn se-btn-ghost" style={{ fontSize: 11 }} onClick={onSelectAllCols}>
          {allSel ? "Deselect all" : "Select all cols"}
        </button>
      </div>

      <table className="se-col-table">
        <thead>
          <tr>
            <th style={{ width: 32 }}>✓</th>
            <th>Column</th>
            <th>Type</th>
            <th>Constraints</th>
            <th>References</th>
          </tr>
        </thead>
        <tbody>
          {table.columns.map(c => {
            const isSel     = selectedCols.has(c.name);
            const refSchema = c.fk_schema || table.schema;
            return (
              <tr key={c.name} className={isSel ? "col-sel" : ""} onClick={() => onColToggle(c.name)}>
                <td>
                  <div className={`se-col-chk${isSel ? " checked" : ""}`}>
                    {isSel && <svg width="8" height="8" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>}
                  </div>
                </td>
                <td>
                  <div className="se-col-name">
                    {c.name}
                    {c.is_primary_key && <span className="se-pk-tag">PK</span>}
                    {c.is_foreign_key && <span className="se-pk-tag se-fk-tag">FK</span>}
                  </div>
                </td>
                <td><span className="se-type-chip">{shortType(c.data_type)}</span></td>
                <td>
                  {!c.nullable && <span className="se-nn-tag">NOT NULL</span>}
                  {c.max_length != null && <span className="se-type-chip" style={{ marginLeft: 4 }}>max {c.max_length}</span>}
                </td>
                <td>
                  {c.is_foreign_key && c.fk_table && (
                    <div className="se-fk-ref" onClick={e => { e.stopPropagation(); onJumpToTable(refSchema, c.fk_table!); }}>
                      <span>→</span>
                      <span>{refSchema !== table.schema ? `${refSchema}.` : ""}{c.fk_table}.{c.fk_column}</span>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── SQL Panel ────────────────────────────────────────────────────────────────

const SQLPanel: React.FC<{
  selectedTables: string[];
  sql: string;
  onSqlChange: (s: string) => void;
  onGenerateSQL: () => void;
}> = ({ selectedTables, sql, onSqlChange, onGenerateSQL }) => (
  <div className="se-sql-panel">
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Custom SQL Query</div>
      {selectedTables.length > 0 && (
        <button className="se-btn se-btn-ghost" style={{ fontSize: 11 }} onClick={onGenerateSQL}>
          ✨ Auto-generate from selection
        </button>
      )}
    </div>
    <div className="se-sql-hint">
      Write a custom query, or auto-generate from your table selection.
      {selectedTables.length > 0 && <span style={{ color: "#4f46e5", fontWeight: 600 }}> {selectedTables.length} table(s) selected.</span>}
    </div>
    <textarea
      className="se-sql-area"
      value={sql}
      onChange={e => onSqlChange(e.target.value)}
      placeholder={"SELECT *\nFROM public.orders o\nJOIN public.customers c ON c.id = o.customer_id\nWHERE o.status = 'active'"}
      spellCheck={false}
    />
    {sql && (
      <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10, color: "#94a3b8" }}>
        {sql.split("\n").length} lines · {sql.length} chars
      </div>
    )}
  </div>
);

// ─── Main SchemaExplorer ──────────────────────────────────────────────────────

export const SchemaExplorer: React.FC<SchemaExplorerProps> = ({
  connectionId, schema, extraSchemas = [],
  onSelectionConfirm, onBack,
}) => {
  const { get } = useApi();

  const allSchemas = useMemo(() => [schema, ...extraSchemas.filter(s => s !== schema)], [schema, extraSchemas]);

  // Table store: fqKey → TableDef
  const [tableMap, setTableMap]       = useState<Record<string, TableDef>>({});
  const [loading, setLoading]         = useState(true);
  const [loadingCols, setLoadingCols] = useState<string | null>(null);
  const [error, setError]             = useState<string | null>(null);

  const [view, setView]               = useState<"list" | "er" | "sql">("list");
  const [search, setSearch]           = useState("");
  const [activeKey, setActiveKey]     = useState<string | null>(null);
  const [visibleSchemas, setVisibleSchemas] = useState<Set<string>>(new Set(allSchemas));

  const [selectedTables, setSelectedTables] = useState<Set<string>>(new Set());
  const [selectedCols, setSelectedCols]     = useState<Record<string, Set<string>>>({});
  const [customSQL, setCustomSQL]           = useState("");
  const [collapsedSchemas, setCollapsedSchemas] = useState<Set<string>>(new Set());

  const tables = useMemo(() => Object.values(tableMap), [tableMap]);

  // Derive FK relationships from column metadata
  const relationships = useMemo<FKRelationship[]>(() => {
    const rels: FKRelationship[] = [];
    const seen = new Set<string>();
    for (const t of tables) {
      for (const c of t.columns) {
        if (!c.is_foreign_key || !c.fk_table || !c.fk_column) continue;
        const ts  = c.fk_schema || t.schema;
        const key = `${t.schema}.${t.name}.${c.name}→${ts}.${c.fk_table}.${c.fk_column}`;
        if (!seen.has(key)) {
          seen.add(key);
          rels.push({ from_schema: t.schema, from_table: t.name, from_column: c.name, to_schema: ts, to_table: c.fk_table, to_column: c.fk_column });
        }
      }
    }
    return rels;
  }, [tables]);

  // Load all schemas
  useEffect(() => {
    if (!connectionId) return;
    setLoading(true); setError(null);

    Promise.all(allSchemas.map(s =>
      get(`/${connectionId}/tables?schema=${encodeURIComponent(s)}`)
        .then((rows: any[]) => rows.map((r: any) => ({ name: r.name, schema: s, row_count: r.row_count, size_bytes: r.size_bytes, columns: [] } as TableDef)))
        .catch(() => [] as TableDef[])
    )).then(async results => {
      const init: Record<string, TableDef> = {};
      for (const g of results) for (const t of g) init[fqKey(t.schema, t.name)] = t;
      setTableMap(init);
      setLoading(false);
      // Eager-load columns for first 10 tables in primary schema
      for (const t of (results[0] || []).slice(0, 10)) {
        await loadColsFor(t.schema, t.name, init);
      }
    }).catch(e => { setError(e.message); setLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId, allSchemas.join(",")]);

  const loadColsFor = useCallback(async (s: string, name: string, cur?: Record<string, TableDef>) => {
    const key  = fqKey(s, name);
    const base = cur || tableMap;
    if (base[key]?.columns.length) return;
    setLoadingCols(key);
    try {
      const cols: ColumnDef[] = await get(`/${connectionId}/columns?schema=${encodeURIComponent(s)}&table=${encodeURIComponent(name)}`);
      setTableMap(prev => ({ ...prev, [key]: { ...prev[key], columns: cols } }));
    } finally { setLoadingCols(null); }
  }, [connectionId, get, tableMap]);

  const handleTableClick = async (s: string, name: string) => {
    setActiveKey(fqKey(s, name));
    await loadColsFor(s, name);
  };

  const handleToggleSchema = (s: string) => {
    setVisibleSchemas(prev => {
      const next = new Set(prev);
      if (next.has(s) && next.size > 1) next.delete(s); else next.add(s);
      return next;
    });
  };

  const handleTableToggle = useCallback((schemaName: string, name: string) => {
    const key = fqKey(schemaName, name);
    setSelectedTables(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        setSelectedCols(c => { const n = { ...c }; delete n[key]; return n; });
      } else {
        next.add(key);
        const t = tableMap[key];
        if (t?.columns.length) {
          setSelectedCols(c => ({ ...c, [key]: new Set(t.columns.map(col => col.name)) }));
        }
      }
      return next;
    });
  }, [tableMap]);

  const handleColToggle = (tableKey: string, col: string) => {
    setSelectedCols(prev => {
      const cols = new Set(prev[tableKey] || []);
      cols.has(col) ? cols.delete(col) : cols.add(col);
      return { ...prev, [tableKey]: cols };
    });
  };

  const handleSelectAllCols = (tableName: string, s: string) => {
    const tableKey = fqKey(s, tableName);
    const t = tableMap[tableKey];
    if (!t) return;
    setSelectedCols(prev => {
      const cols = prev[tableKey] || new Set<string>();
      const allSel = t.columns.every(c => cols.has(c.name));
      return { ...prev, [tableKey]: allSel ? new Set() : new Set(t.columns.map(c => c.name)) };
    });
  };

  const resolveSchema = useCallback((name: string) => {
    for (const s of allSchemas) if (tableMap[fqKey(s, name)]) return s;
    return schema;
  }, [allSchemas, tableMap, schema]);

  const generateSQL = useCallback(() => {
    if (!selectedTables.size) return;
    // selectedTables stores fqKeys like "public.orders"; split back to {schema, name}
    const tbls = Array.from(selectedTables).map(k => {
      const t = tableMap[k]!;
      return { key: k, schema: t.schema, name: t.name };
    });
    if (tbls.length === 1) {
      const { key, schema: s, name } = tbls[0];
      const t     = tableMap[key];
      const cols  = selectedCols[key];
      const cList = cols && cols.size > 0 && cols.size < (t?.columns.length ?? 999)
        ? Array.from(cols).map(c => `  ${name}.${c}`).join(",\n")
        : `  ${name}.*`;
      setCustomSQL(`SELECT\n${cList}\nFROM ${s}.${name}`);
    } else {
      const [base, ...rest] = tbls;
      const colList = ({ key, name }: { key: string; name: string }) => {
        const t    = tableMap[key];
        const cols = selectedCols[key];
        if (!t || !cols || !cols.size || cols.size === t.columns.length) return `  ${name}.*`;
        return Array.from(cols).map(c => `  ${name}.${c} AS ${name}_${c}`).join(",\n");
      };
      const allCols = [colList(base), ...rest.map(colList)].join(",\n");
      const usedJ   = new Set<string>();
      const joins   = [] as string[];
      for (const r of relationships) {
        const fk = fqKey(r.from_schema, r.from_table);
        const tk = fqKey(r.to_schema, r.to_table);
        if (selectedTables.has(fk) && selectedTables.has(tk)) {
          const jk = `${fk}→${tk}`;
          if (!usedJ.has(jk)) {
            usedJ.add(jk);
            joins.push(`LEFT JOIN ${r.from_schema}.${r.from_table} ON ${r.from_table}.${r.from_column} = ${r.to_table}.${r.to_column}`);
          }
        }
      }
      const coveredKeys = new Set([base.key, ...relationships
        .filter(r => selectedTables.has(fqKey(r.from_schema, r.from_table)) && selectedTables.has(fqKey(r.to_schema, r.to_table)))
        .flatMap(r => [fqKey(r.from_schema, r.from_table), fqKey(r.to_schema, r.to_table)])]);
      for (const t of rest) if (!coveredKeys.has(t.key)) joins.push(`CROSS JOIN ${t.schema}.${t.name}`);
      setCustomSQL(`SELECT\n${allCols}\nFROM ${base.schema}.${base.name}${joins.length ? "\n" + joins.join("\n") : ""}`);
    }
    setView("sql");
  }, [selectedTables, selectedCols, tableMap, relationships]);

  const handleConfirm = () => {
    if (view === "sql" && customSQL.trim()) {
      onSelectionConfirm({ tables: [], columns: {}, relationships: [], custom_sql: customSQL.trim() });
      return;
    }
    if (!selectedTables.size) return;
    // selectedTables stores fqKeys; build the output the router expects:
    //   tables: string[]           → bare table names (router maps via tableSchemas)
    //   tableSchemas: {name: schema} → explicit schema for each table
    //   columns: {tableName: col[]} → keyed by bare table name for simplicity
    const activeRels = relationships.filter(r =>
      selectedTables.has(fqKey(r.from_schema, r.from_table)) &&
      selectedTables.has(fqKey(r.to_schema, r.to_table))
    );
    const tableNames: string[] = [];
    const tableSchemas: Record<string, string> = {};
    const colsOut: Record<string, string[]> = {};
    for (const fk of Array.from(selectedTables)) {
      const t = tableMap[fk];
      if (!t) continue;
      tableNames.push(t.name);
      tableSchemas[t.name] = t.schema;
      const cols = selectedCols[fk];
      if (cols?.size) colsOut[t.name] = Array.from(cols);
    }
    onSelectionConfirm({
      tables: tableNames,
      tableSchemas,
      columns: colsOut,
      relationships: activeRels,
    });
  };

  // Sidebar groups
  const sidebarGroups = useMemo(() => {
    const q = search.toLowerCase();
    return allSchemas
      .map(s => ({
        schema: s,
        tables: tables.filter(t => t.schema === s && t.name.toLowerCase().includes(q)).sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .filter(g => g.tables.length > 0);
  }, [allSchemas, tables, search]);

  const activeTable  = activeKey ? tableMap[activeKey] : null;
  const selCount     = selectedTables.size;
  const canConfirm   = selCount > 0 || (view === "sql" && customSQL.trim().length > 0);
  const activeSelCols = activeTable ? (selectedCols[fqKey(activeTable.schema, activeTable.name)] || new Set<string>()) : new Set<string>();

  return (
    <div className="se-root">
      <style>{SE_CSS}</style>

      {/* Toolbar */}
      <div className="se-toolbar">
        {onBack && (
          <button className="se-btn se-btn-ghost" style={{ padding: "5px 12px", fontSize: 12 }} onClick={onBack}>← Back</button>
        )}
        <div>
          <div className="se-toolbar-title">Schema Explorer</div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1, fontFamily: "IBM Plex Mono, monospace" }}>
            {allSchemas.join(" · ")} · {tables.length} tables
          </div>
        </div>
        <div className="se-search">
          <svg width="13" height="13" viewBox="0 0 20 20" fill="#94a3b8">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"/>
          </svg>
          <input placeholder="Filter tables…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="se-view-toggle">
          {(["list", "er", "sql"] as const).map(id => (
            <button key={id} className={`se-view-btn${view === id ? " active" : ""}`} onClick={() => setView(id)}>
              {id === "list" ? "≡ List" : id === "er" ? "⬡ Diagram" : "⌨ SQL"}
            </button>
          ))}
        </div>
        {selCount > 0 && <span className="se-badge">{selCount} selected</span>}
        <button className="se-btn se-btn-primary" disabled={!canConfirm} onClick={handleConfirm}>Use Selection →</button>
      </div>

      {/* Body */}
      {loading ? (
        <div className="se-loading"><Spinner size={20} />Loading schemas…</div>
      ) : error ? (
        <div className="se-empty">
          <div className="se-empty-icon">⚠️</div>
          <div className="se-empty-title">Failed to load schema</div>
          <div className="se-empty-sub">{error}</div>
        </div>
      ) : (
        <div className="se-split">
          {/* Sidebar */}
          <div className="se-sidebar">
            <div className="se-sidebar-hd">
              <span>Tables ({tables.filter(t => t.name.toLowerCase().includes(search.toLowerCase())).length})</span>
              {selCount > 0 && (
                <button style={{ all: "unset", cursor: "pointer", fontSize: 10, color: "#ef4444", fontWeight: 600 }}
                  onClick={() => { setSelectedTables(new Set()); setSelectedCols({}); }}>Clear</button>
              )}
            </div>
            <div className="se-sidebar-list">
              {sidebarGroups.map(g => {
                const isCollapsed = collapsedSchemas.has(g.schema);
                const toggleCollapse = () => setCollapsedSchemas(prev => {
                  const next = new Set(prev);
                  if (next.has(g.schema)) next.delete(g.schema); else next.add(g.schema);
                  return next;
                });
                return (
                  <React.Fragment key={g.schema}>
                    {allSchemas.length > 1 && (
                      <div className="se-schema-group-hd" onClick={toggleCollapse}>
                        <span>📁 {g.schema} <span style={{ color: "#a5b4fc", fontWeight: 400 }}>({g.tables.length})</span></span>
                        <span className={`se-schema-chevron${isCollapsed ? "" : " open"}`}>▶</span>
                      </div>
                    )}
                    {!isCollapsed && g.tables.map(t => {
                      const key    = fqKey(t.schema, t.name);
                      const isSel  = selectedTables.has(fqKey(t.schema, t.name));
                      const isAct  = activeKey === key;
                      const relCnt = relationships.filter(r =>
                        (r.from_schema === t.schema && r.from_table === t.name) ||
                        (r.to_schema   === t.schema && r.to_table   === t.name)
                      ).length;
                      return (
                        <div key={key}
                          className={`se-table-row${isAct ? " active" : ""}${isSel && !isAct ? " checked" : ""}`}
                          onClick={() => handleTableClick(t.schema, t.name)}>
                          <div className={`se-table-chk${isSel ? " checked" : ""}`}
                            onClick={e => { e.stopPropagation(); handleTableToggle(t.schema, t.name); }}>
                            {isSel && <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="se-table-nm">{t.name}</div>
                            <div className="se-table-meta">
                              {t.row_count != null && `${fmtRows(t.row_count)} rows`}
                              {relCnt > 0 && ` · ${relCnt} rel`}
                            </div>
                          </div>
                          {loadingCols === key && <Spinner size={12} />}
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Main */}
          <div className="se-main">
            {view === "list" && (
              <TableDetail
                table={activeTable}
                selectedCols={activeSelCols}
                onColToggle={col => activeTable && handleColToggle(fqKey(activeTable.schema, activeTable.name), col)}
                onSelectAllCols={() => activeTable && handleSelectAllCols(activeTable.name, activeTable.schema)}
                onJumpToTable={(s, n) => handleTableClick(s, n)}
              />
            )}
            {view === "er" && (
              <ERDiagram
                tables={tables}
                allSchemas={allSchemas}
                visibleSchemas={visibleSchemas}
                onToggleSchema={handleToggleSchema}
                selectedTables={selectedTables}
                relationships={relationships}
                onTableToggle={handleTableToggle}
              />
            )}
            {view === "sql" && (
              <SQLPanel
                selectedTables={Array.from(selectedTables)}
                sql={customSQL}
                onSqlChange={setCustomSQL}
                onGenerateSQL={generateSQL}
              />
            )}
          </div>
        </div>
      )}

      {/* Selection footer */}
      {selCount > 0 && (
        <div className="se-sel-footer">
          <div className="se-sel-chips">
            {Array.from(selectedTables).map(fk => {
              const t = tableMap[fk];
              if (!t) return null;
              return (
                <div key={fk} className="se-sel-chip">
                  <span>{allSchemas.length > 1 ? fk : t.name}</span>
                  {selectedCols[fk] && <span style={{ opacity: 0.6, fontSize: 10 }}>({selectedCols[fk].size} cols)</span>}
                  <button className="se-sel-rm" onClick={() => handleTableToggle(t.schema, t.name)}>✕</button>
                </div>
              );
            })}
          </div>
          {relationships.filter(r => selectedTables.has(fqKey(r.from_schema, r.from_table)) && selectedTables.has(fqKey(r.to_schema, r.to_table))).length > 0 && (
            <div style={{ fontSize: 11, color: "#a5b4fc", flexShrink: 0 }}>
              🔗 {relationships.filter(r => selectedTables.has(fqKey(r.from_schema, r.from_table)) && selectedTables.has(fqKey(r.to_schema, r.to_table))).length} rel(s)
            </div>
          )}
          <button className="se-btn se-btn-ghost"
            style={{ fontSize: 11, color: "#c7d2fe", borderColor: "rgba(255,255,255,0.2)", background: "transparent" }}
            onClick={generateSQL}>
            ✨ Preview SQL
          </button>
        </div>
      )}
    </div>
  );
};

export default SchemaExplorer;